import { motion } from "framer-motion";
import { HeartHandshake, Users, Shield, ArrowRight, ExternalLink } from "lucide-react";
import { SectionTitle } from "@/components/SectionTitle";

// Data-driven approach so more can be added later from the admin panel
const IMPACT_DATA = [
  {
    id: 1,
    name: "SHIVAJIRAJAN REDDIAR FOUNDATION",
    role: "Founder",
    description: "A social initiative focused on serving and empowering the community through sustainable development and philanthropic efforts.",
    icon: HeartHandshake,
    websiteUrl: null, // No URL provided yet
  },
  {
    id: 2,
    name: "Reddiar Peramaipu",
    role: "Community Leader",
    description: "A community organization that brings together members, preserves cultural traditions, and promotes community welfare and solidarity.",
    icon: Users,
    websiteUrl: "https://Reddiar.org",
  }
];

export const CommunityImpact = () => {
  return (
    <section id="community-impact" className="section-pad relative overflow-hidden" data-testid="community-impact-section">
      {/* Ambient background glows */}
      <div className="absolute top-1/4 -left-[20%] w-[600px] h-[600px] bg-[hsl(var(--gold))]/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[hsl(var(--gold-deep))]/10 rounded-full blur-[120px] pointer-events-none translate-y-1/3 translate-x-1/3" />
      
      <div className="container-x relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-start">
          
          {/* Left Side: Sticky Intro & Badges */}
          <div className="w-full lg:w-5/12 lg:sticky lg:top-32 shrink-0 flex flex-col gap-8">
            <div className="relative pl-8 md:pl-10">
              {/* Elegant vertical accent line */}
              <div className="absolute left-0 top-2 bottom-0 w-1 rounded-full bg-gradient-to-b from-[hsl(var(--gold-light))] via-[hsl(var(--gold-deep))] to-transparent opacity-80" />
              
              <SectionTitle 
                eyebrow="Beyond The Desk" 
                title={<>Leadership & <br className="hidden lg:block"/><span className="italic gold-text">Impact</span></>} 
                className="text-left !items-start"
              />
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-muted-foreground leading-relaxed mt-6 mb-8 text-lg"
              >
                My professional career is only one part of the journey. I believe true success is measured by the positive impact we leave on our communities. Through leadership and service, I strive to empower others and preserve the values that unite us.
              </motion.p>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex flex-wrap gap-4"
              >
                <div className="glass px-5 py-2.5 rounded-full flex items-center gap-3 border border-[hsl(var(--gold))]/20 shadow-sm shadow-[hsl(var(--gold))]/5 hover:-translate-y-1 transition-transform">
                  <Shield className="w-4 h-4 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" />
                  <span className="text-sm font-medium tracking-wide">Founder</span>
                </div>
                <div className="glass px-5 py-2.5 rounded-full flex items-center gap-3 border border-[hsl(var(--gold))]/20 shadow-sm shadow-[hsl(var(--gold))]/5 hover:-translate-y-1 transition-transform">
                  <Users className="w-4 h-4 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" />
                  <span className="text-sm font-medium tracking-wide">Community Leader</span>
                </div>
              </motion.div>
            </div>
          </div>
          
          {/* Right Side: Staggered Floating Panels */}
          <div className="w-full lg:w-7/12 flex flex-col gap-8 lg:gap-12 lg:pt-16">
            {IMPACT_DATA.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, delay: index * 0.15 }}
                  className={`relative group rounded-3xl glass grain overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_15px_40px_rgba(0,0,0,0.12)] hover:shadow-[hsl(var(--gold))]/10 border border-black/5 hover:border-[hsl(var(--gold))]/40 dark:border-white/5 dark:hover:border-[hsl(var(--gold))]/40 ${index % 2 === 1 ? 'lg:ml-12' : 'lg:mr-12'}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--gold))]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  
                  <div className="p-6 md:p-8 relative z-10 flex flex-col">
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[hsl(var(--gold-light))]/20 to-[hsl(var(--gold-deep))]/20 border border-[hsl(var(--gold))]/30 flex items-center justify-center shadow-inner">
                        <Icon className="w-6 h-6 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" />
                      </div>
                      
                      <div className="px-3 py-1 rounded-full bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                        {item.role}
                      </div>
                    </div>
                    
                    <h3 className="font-serif text-2xl leading-tight mb-3 group-hover:text-[hsl(var(--gold-deep))] dark:group-hover:text-[hsl(var(--gold-light))] transition-colors duration-300">
                      {item.name}
                    </h3>
                    
                    <p className="text-muted-foreground leading-relaxed text-sm mb-2">
                      {item.description}
                    </p>
                    
                    {item.websiteUrl && (
                      <div className="mt-5 pt-5 border-t border-black/5 dark:border-white/5 flex items-center justify-between">
                        <a 
                          href={item.websiteUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))] hover:underline group/btn"
                        >
                          Learn More 
                          <ExternalLink className="w-4 h-4 transition-transform group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" />
                        </a>
                        
                        <div className="w-8 h-8 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                          <ArrowRight className="w-4 h-4 text-foreground/50" />
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
          
        </div>
      </div>
    </section>
  );
};
